import AnimationLoop from '../control-flow/animation-loop';
import Signal from '../control-flow/signal';
import Tool from './tool';
import logging from '../logging';
import { Camera, Euler, Group, Object3D, PerspectiveCamera, Vector3 } from 'three';
import { QUARTER_TURN, TWO_PI } from '../math/constants';
import { deg2rad } from "../math/deg2rad";
import { registerListener } from '../html/register-listener';
import isFreeKeyPress from '../html/is-free-keypress';

const log = logging('tools');

/**
 * The rate of turning for orthographic cameras. The units here are "full turns
 * per canvas width", i.e. if you drag completely across the canvas, how far
 * should the camera turn?
 */
const ORTHO_RATE = 1;

/**
 * Our basic speed in inches per second. A fast walking pace is four miles per
 * hour, but our natural units for speed are inches per second.
 *
 * (4 * 5280 * 12) / (60 * 60) = 70.4
 */
const DEFAULT_WALKING_SPEED = 70.4;

/**
 * The base rotational speed when the left and right arrow keys are pressed
 * expressed in radians per second.
 */
const KEY_ROTATION_SPEED = QUARTER_TURN; // per second

/**
 * When the shift key is depressed, how much faster do the navigation keys
 * act?
 */
const SHIFT_KEY_TIME_SCALE = 2.5;

/**
 * When the slash key (immediately to the left of the right shift key), the
 * keys act much more slowly.
 */
const SLASH_KEY_TIME_SCALE = 0.125;

/**
 * The lowest that the user can use the keyboard to descend.
 */
const MIN_CAMERA_ELEVATION = 2;

const BASE_WHEEL_SPEED = 0.05;

/**
 * How fast dragging the right mouse button moves the view, expressed in inches
 * per CSS pixel.
 */
const BASE_RIGHT_DRAG_SPEED = 0.2;

// A scratch vector for use in our update procedure
const v = new Vector3();

export class FirstPersonTool extends Tool {
    camera: Camera;
    canvas: HTMLCanvasElement;

    // We manipulate the orientation of the dummy instead of manipulating the
    // camera directly. This lets us avoid changing the Euler order of the
    // camera itself.
    private dummy: Object3D;

    onChange = new Signal('onChange', 'tools');
    ts: number;

    // The XY position of the pointer over the canvas in CSS pixels at the
    // moment the pointerdown event occurred
    startX: number;
    startY: number;

    startCamRX: number;
    startCamRY: number;

    pivot = new Group();

    isPrimaryDown: boolean = false;
    isRightDown: boolean = false;
    keysDown: Set<string> = new Set();

    // Note that the loop runs continuously but it only calls our update method
    // when the mouse or a relevant keyboard key is pressed
    loop = new AnimationLoop(() => {
        const
            now = performance.now(),
            delta = (now - this.ts) / 1000;
        this.ts = now;

        if(this.keysDown.size > 0) {
            this.update(delta);
        }
    });

    constructor(camera: Camera, canvas: HTMLCanvasElement) {
        super();
        this.camera = camera;
        this.canvas = canvas;
        this.dummy = new Object3D();
    }

    enable(): void {
        log.debug('FirstPersonTool.enable()');
        this.ts = performance.now();
        this.loop.start();

        this.dummy.position.copy(this.camera.position);
        this.dummy.rotation.copy(this.camera.rotation);
        this.dummy.rotation.reorder('YXZ');

        registerListener(this.canvas, 'pointerdown', this.begin);

        registerListener(window, 'keydown', this.keyDown);
        registerListener(window, 'keyup', this.keyUp);
        registerListener(this.canvas, 'wheel', this.wheel);
        registerListener(this.canvas, 'contextmenu', this.disableContextMenu);
    }

    disable(): void {
        this.canvas.removeEventListener('pointerdown', this.begin);
        removeEventListener('pointerup', this.end);
        removeEventListener('pointermove', this.pointerMove);
        removeEventListener('keydown', this.keyDown);
        removeEventListener('keyup', this.keyUp);
        this.canvas.removeEventListener('wheel', this.wheel);
        this.canvas.removeEventListener('contextmenu', this.disableContextMenu);
        this.loop.stop();
    }

    /**
     * Begin and end pair to handle the pointer events. The keyboard events
     * are handled by keyDown and keyUp.
     */
    begin(event: PointerEvent): void {
        // left mouse button
        if(event.button == 0) {
            registerListener(window, 'pointerup', this.end, {once: true});
            registerListener(window, 'pointermove', this.pointerMove);

            this.isPrimaryDown = true;
            this.startX = event.clientX;
            this.startY = event.clientY;
            this.startCamRX = this.dummy.rotation.x;
            this.startCamRY = this.dummy.rotation.y;
        }
        // right mouse button
        else if(event.button == 2)
        {
            registerListener(window, 'pointerup', this.end, {once: true});
            registerListener(window, 'pointermove', this.pointerMove);

            this.isRightDown = true;
            this.startX = event.clientX;
            this.startY = event.clientY;
        }
    }

    pointerMove = (event: PointerEvent) => {
        if(this.isPrimaryDown) {
            const
                dx = event.clientX - this.startX,
                dy = event.clientY - this.startY,
                // The rate of turn as radians per pixel
                rate = this.camera instanceof PerspectiveCamera
                    ? deg2rad(this.camera.fov) / this.canvas.clientHeight
                    : TWO_PI * ORTHO_RATE / this.canvas.clientWidth;

            this.dummy.rotation.x = this.startCamRX + dy * rate;
            this.dummy.rotation.y = this.startCamRY + dx * rate;
        } else if(this.isRightDown) {
            let speed = BASE_RIGHT_DRAG_SPEED;
            if(this.keysDown.has('shift')) {
                speed *= SHIFT_KEY_TIME_SCALE;
            } else if(this.keysDown.has('/')) {
                speed *= SLASH_KEY_TIME_SCALE;
            }
            v.set(0, 0, -1);
            applyHeadingMovement(this.dummy.position,
                                 v,
                                 this.dummy.rotation,
                                 speed * event.movementY);
            v.set(-1, 0, 0);
            applyHeadingMovement(this.dummy.position,
                                 v,
                                 this.dummy.rotation,
                                 speed * event.movementX);
        }
        this.updateAndTriggerChange();
    }

    end(): void {
        removeEventListener('pointermove', this.pointerMove);
        this.isPrimaryDown = false;
        this.isRightDown = false;
    }

    cancel(): void {
        // nothing to do
    }

    keyDown = (event: KeyboardEvent) => {
        if(ignoreKey(event)) return;
        this.keysDown.add(event.key.toLowerCase());
    };

    keyUp = (event: KeyboardEvent) => {
        // This will not throw an exception when the key is not in the set
        this.keysDown.delete(event.key.toLowerCase());
    };

    wheel = (event: WheelEvent) => {
        let speed = BASE_WHEEL_SPEED;
        if(this.keysDown.has('shift')) {
            speed *= SHIFT_KEY_TIME_SCALE;
        } else if(this.keysDown.has('/')) {
            speed *= SLASH_KEY_TIME_SCALE;
        }

        if(event.deltaY != 0) {
            v.set(0, 0, 1);
            applyHeadingMovement(this.dummy.position,
                                 v,
                                 this.dummy.rotation,
                                 speed * event.deltaY);
        }
        this.updateAndTriggerChange();
    }

    /**
     * @param delta - the time, in seconds, since the last frame
     */
    update(delta: number) {
        // In order to speed up or slow down all the keys, we pretend that more
        // or less time has passed
        if(this.keysDown.has('shift')) {
            delta *= SHIFT_KEY_TIME_SCALE;
        } else if(this.keysDown.has('/')) {
            delta *= SLASH_KEY_TIME_SCALE;
        }

        for(const key of this.keysDown) {
            switch(key) {
                case 'a':
                    v.set(-1, 0, 0);
                    applyHeadingMovement(this.dummy.position,
                                         v,
                                         this.dummy.rotation,
                                         DEFAULT_WALKING_SPEED * delta);
                    this.updateAndTriggerChange();
                    break;
                case 'arrowup':
                case 'w':
                    v.set(0, 0, -1);
                    applyHeadingMovement(this.dummy.position,
                                         v,
                                         this.dummy.rotation,
                                         DEFAULT_WALKING_SPEED * delta);
                    this.updateAndTriggerChange();
                    break;
                case 'arrowdown':
                case 's':
                    v.set(0, 0, 1);
                    applyHeadingMovement(this.dummy.position,
                                         v,
                                         this.dummy.rotation,
                                         DEFAULT_WALKING_SPEED * delta);
                    this.updateAndTriggerChange();
                    break;
                case 'd':
                    v.set(1, 0, 0);
                    applyHeadingMovement(this.dummy.position,
                                         v,
                                         this.dummy.rotation,
                                         DEFAULT_WALKING_SPEED * delta);
                    this.updateAndTriggerChange();
                    break;
                case 'z':
                    this.dummy.position.y = Math.max(
                        MIN_CAMERA_ELEVATION,
                        this.dummy.position.y - DEFAULT_WALKING_SPEED * delta);
                    this.updateAndTriggerChange();
                    break;
                case 'x':
                    this.dummy.position.y += DEFAULT_WALKING_SPEED * delta;
                    this.updateAndTriggerChange();
                    break;
                case 'arrowleft':
                    this.dummy.rotation.y += KEY_ROTATION_SPEED * delta;
                    this.updateAndTriggerChange();
                    break;
                case 'arrowright':
                    this.dummy.rotation.y -= KEY_ROTATION_SPEED * delta;
                    this.updateAndTriggerChange();
                    break;
            }

            v.applyEuler(this.dummy.rotation);
            v.y = 0;
            v.normalize();
        }
    }

    updateAndTriggerChange() {
        this.camera.position.copy(this.dummy.position);
        this.camera.rotation.copy(this.dummy.rotation);
        this.camera.rotation.reorder('XYZ');

        this.onChange.trigger();
    }

    disableContextMenu = (event: MouseEvent) => {
        event.preventDefault();
    }
}
export default FirstPersonTool

const ignoreKey = (event: KeyboardEvent): boolean => {
    // We don't want to capture events from input sources
    if(!isFreeKeyPress(event)) return true;
    // If any modifier keys are pressed, we'll ignore that too
    if(event.altKey || event.ctrlKey || event.metaKey) {
        return true;
    }
    return false;
}

/**
 * This enables moving the camera in the direction it is facing by specifying
 * an axis vector relative to the camera's direction given by `dir`.
 *
 * Rotates the given `axis` (a unit vector) according to the given euler `dir`,
 * discarding the vertical component, and adds it to the object's position.
 * This uses `axis` as a scratch vector, so its value will be overwritten by
 * the relative offset applied to the `obj` vector.
 */
const applyHeadingMovement = (obj: Vector3, axis: Vector3, dir: Euler, distance: number) => {
    axis.applyEuler(dir);
    axis.y = 0;
    axis.normalize();
    axis.multiplyScalar(distance);
    obj.add(axis);
}

